import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge } from 'reactstrap';

import { getErrorMessage } from '~libs/axiosWrapper';
import { formatDateString } from '~libs/dayjs';

import { ErrorResponse, ModalCard, Spinner } from '~components';
import { InputProps } from '~components/form-fields';
import { Column, TableView } from '~components/table-view';

import { BudgetSahPreview, SahAssignment, useBulkSahPreviewMutation } from '~support-at-home/actions/budgets';

import MatchBadge from './MatchBadge';
import { getMatchType, MatchType } from './matchType';
import SahBudgetCell from './SahBudgetCell';

type FilterTab = 'all' | MatchType;

const TABS: Array<{ key: FilterTab; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'auto', label: 'Auto-matched' },
  { key: 'choose', label: 'Choose required' },
  { key: 'no-match', label: 'No match' },
];

interface ReviewValue {
  budget_ids: string[];
  assignments?: SahAssignment[];
}

interface ComponentProps extends InputProps {
  value: ReviewValue;
  onChange?: (name: string, value: any) => void;
}

const ReviewMatches: React.FC<ComponentProps> = ({ value, onChange }) => {
  const { budget_ids } = value;

  const [bulkSahPreview] = useBulkSahPreviewMutation();
  const [previewData, setPreviewData] = useState<BudgetSahPreview[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<any>(null);
  const hasFetched = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [radioSelections, setRadioSelections] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState<FilterTab>('all');

  useEffect(() => {
    if (!budget_ids?.length || hasFetched.current) return;
    hasFetched.current = true;
    const CHUNK_SIZE = 100;
    const chunks: string[][] = [];
    for (let i = 0; i < budget_ids.length; i += CHUNK_SIZE) {
      chunks.push(budget_ids.slice(i, i + CHUNK_SIZE));
    }
    setIsLoading(true);
    setError(null);
    Promise.all(chunks.map((chunk) => bulkSahPreview({ budget_ids: chunk }).unwrap()))
      .then((results) => {
        setPreviewData(results.flat());
        setIsLoading(false);
      })
      .catch((err) => {
        setError(err);
        setIsLoading(false);
      });
  }, []);

  // Auto-check and auto-select radio for single-candidate (auto) rows on load
  useEffect(() => {
    if (!previewData) return;
    const initialChecked = new Set<string>();
    const initialRadio: Record<string, string> = {};
    previewData.forEach((preview) => {
      if (preview.candidates.length === 1) {
        initialChecked.add(preview.budget_id);
        initialRadio[preview.budget_id] = preview.candidates[0].id;
      } else if (
        preview.current_sah_budget_id &&
        preview.candidates.some((c) => c.id === preview.current_sah_budget_id)
      ) {
        initialChecked.add(preview.budget_id);
        initialRadio[preview.budget_id] = preview.current_sah_budget_id;
      }
    });
    setChecked(initialChecked);
    setRadioSelections(initialRadio);
  }, [previewData]);

  // Emit assignments whenever checked or radioSelections change.
  useEffect(() => {
    if (!previewData) return;
    const assignments = previewData
      .filter((preview) => checked.has(preview.budget_id))
      .map((preview) => ({
        budget_id: preview.budget_id,
        sah_budget_id: radioSelections[preview.budget_id] ?? null,
      }));
    onChangeRef.current?.('assignments', assignments);
  }, [checked, radioSelections, previewData]);

  const counts = useMemo<Record<MatchType, number>>(() => {
    const acc: Record<MatchType, number> = { auto: 0, choose: 0, 'no-match': 0 };
    previewData?.forEach((preview) => {
      acc[getMatchType(preview)] += 1;
    });
    return acc;
  }, [previewData]);

  // Map previewData to include `id` field required by TableView
  const tableData = useMemo(() => previewData?.map((p) => ({ ...p, id: p.budget_id })) ?? [], [previewData]);

  const filteredTableData = useMemo(
    () => (activeTab === 'all' ? tableData : tableData.filter((p) => getMatchType(p) === activeTab)),
    [tableData, activeTab],
  );

  const assignedCount = useMemo(
    () =>
      previewData ? previewData.filter((p) => checked.has(p.budget_id) && !!radioSelections[p.budget_id]).length : 0,
    [checked, radioSelections, previewData],
  );

  const selectRadio = useCallback((budgetId: string, sahBudgetId: string) => {
    setRadioSelections((prev) => ({ ...prev, [budgetId]: sahBudgetId }));
    setChecked((prev) => new Set(prev).add(budgetId));
  }, []);

  const handleRowSelectionChange = useCallback(
    (ids: (string | number)[]) => {
      // The selection event only governs the rows visible on the active tab. Selections made
      // on other tabs must be preserved, so merge against the visible rows rather than replacing
      // the whole Set (a header-checkbox deselect on a filtered tab would otherwise wipe them).
      const visibleSelectableIds = new Set(
        filteredTableData
          .map((row) => row.budget_id)
          .filter((id) => {
            const preview = previewData?.find((p) => p.budget_id === id);
            return preview && getMatchType(preview) !== 'no-match';
          }),
      );
      const newVisibleChecked = new Set(ids.map(String).filter((id) => visibleSelectableIds.has(id)));

      // Preserve off-tab selections; apply the visible tab's new selection state.
      const newChecked = new Set([...checked].filter((id) => !visibleSelectableIds.has(id)));
      newVisibleChecked.forEach((id) => newChecked.add(id));

      setRadioSelections((prev) => {
        const next = { ...prev };
        // Auto-select candidate for newly checked auto rows
        newVisibleChecked.forEach((budgetId) => {
          if (!checked.has(budgetId)) {
            const preview = previewData?.find((p) => p.budget_id === budgetId);
            if (preview && getMatchType(preview) === 'auto' && preview.candidates[0]) {
              next[budgetId] = preview.candidates[0].id;
            }
          }
        });
        // Clear radio for auto rows just unchecked on the visible tab
        visibleSelectableIds.forEach((budgetId) => {
          if (checked.has(budgetId) && !newVisibleChecked.has(budgetId)) {
            const preview = previewData?.find((p) => p.budget_id === budgetId);
            if (preview && getMatchType(preview) === 'auto') {
              delete next[budgetId];
            }
          }
        });
        return next;
      });

      setChecked(newChecked);
    },
    [checked, previewData, filteredTableData],
  );

  // Columns are defined inline (not memoised) so componentProps closures always
  // capture the latest radioSelections and checked values.
  const columns: Column[] = [
    { id: 'reference', header: 'Reference', type: 'text' },
    { id: 'client_name', header: 'Client', type: 'text' },
    {
      id: 'dates',
      header: 'Budget Dates',
      type: 'text',
      deriveValue: (row: any) => `${formatDateString(row.start_date)} – ${formatDateString(row.end_date)}`,
    },
    { id: 'funding_type_name', header: 'Funding Type', type: 'text' },
    {
      id: 'match_status',
      header: 'Status',
      type: 'custom',
      component: MatchBadge,
      componentProps: (row: any) => ({ type: getMatchType(row) }),
    },
    {
      id: 'sah_budget',
      header: 'SAH Budget',
      type: 'custom',
      component: SahBudgetCell,
      componentProps: (row: any) => {
        const matchType = getMatchType(row);
        return {
          preview: row,
          matchType,
          selectedSahId: radioSelections[row.budget_id] ?? null,
          onSelect: selectRadio,
          // A checked "choose" row with no SAH budget selected is incomplete. Surfacing this
          // inline immediately pairs with the wizard's disableNext, which blocks advancement
          // until every checked row is resolved — no cross-component invalid signal needed.
          showRowError: checked.has(row.budget_id) && !radioSelections[row.budget_id] && matchType === 'choose',
        };
      },
    },
  ];

  if (isLoading) return <Spinner loading />;
  if (error) return <ErrorResponse message={getErrorMessage(error)} />;
  if (!previewData) return null;

  return (
    <div>
      <ModalCard header="Match Summary" className="w-100 mb-3">
        <div className="d-flex gap-3 p-2">
          <span>
            <Badge color="success" className="me-1">
              {counts.auto}
            </Badge>{' '}
            Auto-matched
          </span>
          <span>
            <Badge color="warning" className="me-1">
              {counts.choose}
            </Badge>{' '}
            Need selection
          </span>
          <span>
            <Badge color="danger" className="me-1">
              {counts['no-match']}
            </Badge>{' '}
            No match
          </span>
          <span className="ms-auto fw-semibold">{assignedCount} will be assigned</span>
        </div>
      </ModalCard>

      <div className="d-flex gap-2 mb-3">
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            className={`btn btn-sm ${activeTab === key ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => setActiveTab(key)}
          >
            {label} ({key === 'all' ? previewData.length : counts[key]})
          </button>
        ))}
      </div>

      <TableView
        id="bulk-sah-assign-review-table"
        columns={columns}
        data={filteredTableData}
        isLoading={false}
        readonly
        canSelect
        selectedIds={Array.from(checked)}
        onRowSelectionChange={handleRowSelectionChange}
        isRowDisabled={(row: any) => getMatchType(row) === 'no-match'}
        striped
        emptyMessage="No budgets to review"
      />
    </div>
  );
};

export default ReviewMatches;
